// Survey Response Types and Enums (Frontend)

import type { EmojiType } from '../question/types';

export enum SurveyResponseStatus {
  PARTIAL = 'PARTIAL',
  COMPLETED = 'COMPLETED',
}

export enum AnswerValueType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  BOOLEAN = 'BOOLEAN',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
}

export enum QuestionType {
  YES_NO = 'YES_NO',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  RANGE = 'RANGE',
}

// Survey Response UI State
export interface SurveyResponseState {
  currentQuestionIndex: number;
  totalQuestions: number;
  responses: Record<string, AnswerData>;
  sessionId: string;
  surveyInstanceId: string;
  language: 'en' | 'no';
  isSubmitting: boolean;
  hasStarted: boolean;
  isCompleted: boolean;
}

// Answer Data for local state management
export interface AnswerData {
  questionId: string;
  value?: string | string[] | number | boolean;
  comment?: string;
  answeredAt?: string;
}

// Question Navigation State
export interface NavigationState {
  canGoBack: boolean;
  canGoNext: boolean;
  canComplete: boolean;
  currentQuestion?: SurveyQuestionData;
  nextQuestion?: SurveyQuestionData;
  previousQuestion?: SurveyQuestionData;
}

// Survey Question Data (normalized from DTOs)
export interface SurveyQuestionData {
  id: string;
  type: QuestionType;
  title: Record<string, string>;
  description?: Record<string, string>;
  order: number;
  isRequired: boolean;
  hasCommentField: boolean;
  commentFieldDescription?: Record<string, string>;
  options?: Array<{
    id: string;
    label: Record<string, string>;
    order: number;
    color?: string;
    emoji?: EmojiType;
  }>;
  rangeConfig?: {
    min: number;
    max: number;
    step: number;
    minLabel?: Record<string, string>;
    maxLabel?: Record<string, string>;
    displayType: 'slider' | 'stars' | 'buttons' | 'scale';
  };
  validationRules?: {
    minLength?: number;
    maxLength?: number;
    minValue?: number;
    maxValue?: number;
    pattern?: string;
    customMessage?: Record<string, string>;
  };
}

// Survey Data (normalized from DTOs)
export interface SurveyData {
  id: string;
  title: string;
  description?: string;
  questions: SurveyQuestionData[];
  totalQuestions: number;
}

// Anonymous Session Context
export interface AnonymousSessionContext {
  sessionId: string;
  surveyInstanceId: string;
  userAgent?: string;
  language?: string;
  startedAt?: string;
}

// Response Submission Context
export interface ResponseSubmissionContext {
  surveyInstanceId: string;
  sessionId: string;
  responses: AnswerData[];
  status: SurveyResponseStatus;
  language: string;
}

// Progress Tracking
export interface ProgressData {
  totalQuestions: number;
  answeredQuestions: number;
  percentageComplete: number;
  currentQuestionIndex: number;
}

// Validation Result
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  questionId: string;
  errorType: 'REQUIRED' | 'INVALID_FORMAT' | 'OUT_OF_RANGE' | 'CUSTOM';
  message: string;
}

export interface ValidationWarning {
  questionId: string;
  warningType: 'INCOMPLETE' | 'SUGGESTION';
  message: string;
}

// Local Storage Keys
export const STORAGE_KEYS = {
  SESSION_ID: 'survey_session_id',
  RESPONSE_DATA: 'survey_response_data',
  CURRENT_QUESTION: 'survey_current_question',
  LANGUAGE: 'survey_language',
  STARTED_SURVEYS: 'survey_started_instances',
  COMPLETED_SURVEYS: 'survey_completed_instances',
} as const;

// Component Props Types
export interface QuestionComponentProps {
  question: SurveyQuestionData;
  value?: string | string[] | number | boolean;
  comment?: string;
  language: 'en' | 'no';
  onChange: (value: string | string[] | number | boolean | undefined) => void;
  onCommentChange?: (comment: string) => void;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
}

export interface NavigationProps {
  onNext: () => void;
  onBack: () => void;
  onComplete: () => void;
  canGoBack: boolean;
  canGoNext: boolean;
  canComplete: boolean;
  isSubmitting?: boolean;
  language: 'en' | 'no';
}

export interface ProgressIndicatorProps {
  currentIndex: number;
  totalQuestions: number;
  completedQuestions: number[];
}

// Language Toggle Props
export interface LanguageToggleProps {
  currentLanguage: 'en' | 'no';
  onLanguageChange: (language: 'en' | 'no') => void;
  disabled?: boolean;
}

// Survey Layout Props
export interface SurveyResponseLayoutProps {
  children: React.ReactNode;
  language: 'en' | 'no';
  onLanguageChange: (language: 'en' | 'no') => void;
  showProgress?: boolean;
  currentStep?: number;
  totalSteps?: number;
}

// Welcome Screen Props
export interface WelcomeScreenProps {
  survey: SurveyData;
  language: 'en' | 'no';
  onStart: () => void;
  estimatedTimeMinutes?: number;
}

// Completion Screen Props
export interface CompletionScreenProps {
  survey: SurveyData;
  language: 'en' | 'no';
  responseId?: string;
}

// Error types for better error handling
export interface SurveyResponseError {
  type:
    | 'SURVEY_EXPIRED'
    | 'SURVEY_NOT_FOUND'
    | 'SESSION_INVALID'
    | 'NETWORK_ERROR'
    | 'SUBMISSION_FAILED';
  message: string;
  details?: unknown;
}

// Question display configuration
export interface QuestionDisplayConfig {
  showQuestionNumber: boolean;
  showProgress: boolean;
  allowSkip: boolean;
  autoAdvance: boolean;
  showCommentField: boolean;
}

// Survey response configuration
export interface SurveyResponseConfig {
  allowMultipleAttempts: boolean;
  sessionTimeoutMinutes: number;
  autoSaveInterval: number;
  maxRetries: number;
  questionDisplay: QuestionDisplayConfig;
}
